import DeliveryAreaFees from '@/components/forms/Locations/DeliveryAreaFees'
import DeliveryServiceAreas from '@/components/forms/Locations/DeliveryServiceAreas/DeliveryServiceAreas'
import FulfillmentSettingsInputs from '@/components/forms/Locations/FulfillmentSettingsInputs'
import WeeklyHoursInputs from '@/components/forms/Locations/WeeklyHoursInputs'
import SaveBar from '@/components/forms/SaveBar'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { ReadOnly } from '@/lib/casl'
import { getDefaultHours } from '@/lib/constants/businessHours'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { PolygonType } from '@/lib/enums/PolygonType'
import { useAppForm } from '@/lib/form'
import {
  locationFilters,
  locationQueries,
  useLocation,
  useUpdateLocationDeliveryAvailabilitySettingsMutation,
  useUpdateLocationDeliveryPricingSettingsMutation,
} from '@/lib/services/locations/locations.query'
import {
  createLocationDeliveryPricingSettingsSchema,
  locationDeliveryAvailabilitySettingsSchema,
  locationIdSchema,
} from '@/lib/services/locations/locations.schema'
import { getErrorMessage } from '@/lib/utils'

import { DeliveryAndAvailabilityHeader } from '@/components/locations/DeliveryAndAvailabilityHeader'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { AlertTriangle, Truck } from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

const filters = locationFilters().get()
const DEFAULT_DELIVERY_CUTOFF_HOURS = 4
const canViewLocationDeliveryPage = (ability) =>
  ability.can('list', 'locations') &&
  (ability.can('view', 'location-delivery') ||
    ability.can('edit', 'location-delivery') ||
    ability.can('view', 'location-delivery-pricing') ||
    ability.can('edit', 'location-delivery-pricing'))

const isPricedArea = (area) =>
  area.fee !== null &&
  area.fee !== '' &&
  (area.polygon_type === PolygonType.Base.Value || area.polygon_type === PolygonType.Location.Value)

export const Route = createFileRoute(
  '/_authenticated/$orgId/settings/locations/$locationId/deliveries/'
)({
  component: LocationDeliverySettings,
  head: adminRouteTitleHead,

  params: {
    parse: (params) => locationIdSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    if (!canViewLocationDeliveryPage(context.ability)) throw notFound()

    const canEditAvailability = context.ability.can('edit', 'location-delivery')
    const canEditPricing = context.ability.can('edit', 'location-delivery-pricing')

    await Promise.all([
      context.queryClient.ensureQueryData(
        locationQueries.show({
          organizationId: params.orgId,
          locationId: params.locationId,
          filters,
        })
      ),
      // Also load all locations to show their delivery areas on the map.
      context.queryClient.ensureQueryData(
        locationQueries.index({ organizationId: params.orgId, filters })
      ),
    ])

    return {
      canEditAvailability,
      canEditPricing,
      isReadOnly: !canEditAvailability && !canEditPricing,
    }
  },
})

export function LocationDeliverySettings() {
  const { locationId } = Route.useParams()
  const { canEditAvailability, canEditPricing } = Route.useLoaderData()
  const { data: location } = useLocation(locationId, { filters })
  const updateLocationAvailabilityMutation = useUpdateLocationDeliveryAvailabilitySettingsMutation()
  const updateLocationPricingMutation = useUpdateLocationDeliveryPricingSettingsMutation()
  const [showFreeDeliveryWarning, setShowFreeDeliveryWarning] = useState(false)
  const [pendingFormValues, setPendingFormValues] = useState(null)
  const deliveryRulesRef = useRef(null)

  // Sort postal code fees by fee ascending on initial load
  const sortedPostalCodeFees = [...(location?.delivery_postal_code_fees ?? [])].sort(
    (a, b) => parseFloat(a.fee || 0) - parseFloat(b.fee || 0)
  )
  const deliveryHours =
    location?.delivery_hours?.length > 0 ? location.delivery_hours : getDefaultHours()
  const availabilityDefaults = {
    is_delivery: location?.is_delivery ?? false,
    delivery_cutoff: location?.delivery_cutoff ?? DEFAULT_DELIVERY_CUTOFF_HOURS,
    delivery_hours: deliveryHours,
  }
  const pricingDefaults = {
    is_distance_pricing_enabled: location?.is_distance_pricing_enabled ?? false,
    is_delivery_cutoff_overrides_enabled: location?.is_delivery_cutoff_overrides_enabled ?? false,
    delivery_max_distance_miles: location?.delivery_max_distance_miles ?? null,
    delivery_postal_code_fees: sortedPostalCodeFees,
    delivery_city_fees: location?.delivery_city_fees ?? [],
    delivery_distance_fee_ranges: location?.delivery_distance_fee_ranges ?? [],
    delivery_area_fees: location?.delivery_area_fees ?? [],
  }
  const pricingValidationSchema = createLocationDeliveryPricingSettingsSchema({
    isDeliveryEnabled: location?.is_delivery !== false,
  })
  const hasFreeDeliveryFees = (fees, isIncluded = () => true) => {
    if (!fees || fees.length === 0) return false
    return fees.some((fee) => {
      const feeAmount = parseFloat(fee.fee || 0)
      return feeAmount === 0 && isIncluded(fee)
    })
  }

  const availabilityForm = useAppForm({
    defaultValues: availabilityDefaults,
    validators: {
      onSubmit: locationDeliveryAvailabilitySettingsSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (!canEditAvailability) {
          return
        }

        try {
          await updateLocationAvailabilityMutation.mutateAsync({
            locationId,
            previousIsDelivery: location?.is_delivery ?? false,
            ...value,
          })

          formApi.reset(value)
          toast.success('Delivery availability saved successfully')
        } catch (error) {
          toast.error('Failed to save delivery availability', {
            description: getErrorMessage(error),
          })
          throw error
        }
      },
    },
    onSubmitInvalid: ({ formApi }) => {
      if (formApi.state.errors?.length > 0) {
        toast.error('Please fix the errors', {
          description: 'There are delivery availability errors that need to be corrected.',
        })
      }
    },
  })

  const pricingForm = useAppForm({
    defaultValues: pricingDefaults,
    validators: {
      onChange: pricingValidationSchema,
      onSubmit: pricingValidationSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (!canEditPricing) {
          return
        }

        const hasFreeFees =
          hasFreeDeliveryFees(value.delivery_postal_code_fees, (fee) =>
            Boolean(fee.postal_code?.trim())
          ) ||
          hasFreeDeliveryFees(value.delivery_city_fees, (fee) => Boolean(fee.city?.trim())) ||
          hasFreeDeliveryFees(
            value.delivery_distance_fee_ranges,
            (fee) => Number(fee.max_miles) > Number(fee.min_miles)
          ) ||
          hasFreeDeliveryFees(
            value.delivery_area_fees,
            (fee) =>
              fee.is_active !== false && isPricedArea(fee) && Boolean(fee.encoded_polygon?.trim())
          )

        if (hasFreeFees) {
          setPendingFormValues({ value, formApi })
          setShowFreeDeliveryWarning(true)
          return
        }

        await performPricingSave({ value, formApi })
      },
    },
    onSubmitInvalid: ({ formApi }) => {
      if (formApi.state.errors?.length > 0) {
        toast.error('Please fix the errors', {
          description: 'There are delivery pricing errors that need to be corrected.',
        })
      }
    },
  })

  const performPricingSave = async ({ value, formApi }) => {
    const pricingValues = { ...value }
    delete pricingValues.is_delivery

    try {
      await updateLocationPricingMutation.mutateAsync({
        locationId,
        ...pricingValues,
      })

      formApi.reset(value)

      toast.success('Delivery pricing saved successfully')
    } catch (error) {
      toast.error('Failed to save delivery pricing', {
        description: getErrorMessage(error),
      })
      throw error
    }
  }

  const handleConfirmSave = async () => {
    if (pendingFormValues) {
      setShowFreeDeliveryWarning(false)
      await performPricingSave(pendingFormValues)
      setPendingFormValues(null)
    }
  }

  const handleFixFreeZipCodes = () => {
    setShowFreeDeliveryWarning(false)
    setPendingFormValues(null)
    // Scroll to delivery rules section
    if (deliveryRulesRef.current) {
      deliveryRulesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <div className="w-full">
      <DeliveryAndAvailabilityHeader
        location={location}
        icon={Truck}
        title="Delivery Settings"
        description="Manage delivery availability, hours, and pricing"
      />

      <div className="flex flex-col gap-4">
        <ReadOnly isReadOnly={!canEditAvailability}>
          <availabilityForm.AppForm aria-label="Delivery Availability Form">
            <div className="flex flex-col gap-4">
              <FulfillmentSettingsInputs
                title="Delivery Service Settings"
                subtitle="Control whether this location offers delivery, when it is available, and how many hours before close an order must be placed."
                fulfillmentSettings={{
                  fulfillment_type: FulfillmentType.Delivery.Value,
                  is_fulfillment_enabled: location?.is_delivery ?? false,
                  cutoff: location?.delivery_cutoff ?? DEFAULT_DELIVERY_CUTOFF_HOURS,
                }}
              />

              <availabilityForm.Subscribe selector={(state) => state.values.is_delivery}>
                {(isDelivery) =>
                  isDelivery ? (
                    <WeeklyHoursInputs
                      title="Delivery Hours"
                      subtitle="Set your delivery hours for each day of the week."
                      initialHours={deliveryHours}
                      fieldName="delivery_hours"
                    />
                  ) : null
                }
              </availabilityForm.Subscribe>
            </div>

            {canEditAvailability ? (
              <SaveBar
                mutation={updateLocationAvailabilityMutation}
                submitButtonText="Save Availability"
              />
            ) : null}
          </availabilityForm.AppForm>
        </ReadOnly>

        <ReadOnly isReadOnly={!canEditPricing}>
          <pricingForm.AppForm aria-label="Delivery Pricing Form">
            <Card>
              <CardHeader className="space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <CardTitle className="mb-2 flex items-center gap-2">
                      <Truck className="h-5 w-5" />
                      Delivery Fees
                    </CardTitle>
                    <Typography variant="supporting">
                      Set the service areas, distance ranges, and delivery fees customers pay.
                    </Typography>
                  </div>
                </div>
              </CardHeader>
              <CardContent ref={deliveryRulesRef} className="space-y-6">
                <DeliveryServiceAreas location={location} />
                <div className="border-t pt-5">
                  <DeliveryAreaFees location={location} />
                </div>
              </CardContent>
            </Card>

            {canEditPricing ? (
              <SaveBar mutation={updateLocationPricingMutation} submitButtonText="Save Pricing" />
            ) : null}
          </pricingForm.AppForm>
        </ReadOnly>
      </div>

      <AlertDialog open={showFreeDeliveryWarning} onOpenChange={setShowFreeDeliveryWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="inline-flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Free Delivery Detected
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have delivery areas with $0 (free) delivery fees. These areas will be delivered to
              for free. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleFixFreeZipCodes}>
              Fix Free Delivery Fees
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave} asChild>
              <Button>Confirm and Save Anyway</Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
